// Client-side bridge to the native Apple IAP purchase → server-verify
// flow. Called by handleUnlock in src/App.jsx on the native branch
// (Capacitor.isNativePlatform() === true). Web path uses Stripe
// Checkout via createCheckoutSessionUnlock (src/lib/stripe.js) — this
// module is native-only.
//
// Flow:
//   deriveContestClass(pool) → Apple productId → AppleIap.purchase() →
//   { jws, transactionId } → POST /api/apple-iap/verify → server verifies
//   Apple's signature + calls unlock_group RPC → { ok, transaction_id }
//
// Trust boundary: the server does the authoritative Apple JWS verification
// (via @apple/app-store-server-library's SignedDataVerifier). This client
// module just plumbs the jws through; it never inspects or trusts its
// contents.

import { registerPlugin } from '@capacitor/core'
import { supabase } from './supabase'
import { deriveContestClass } from './stripe'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// Native plugin handle. registerPlugin creates a JS proxy that dispatches
// to the Swift implementation (ios/App/CapApp-SPM/Sources/CapApp-SPM/
// AppleIap.swift) via Capacitor's bridge. On web, calls throw UNIMPLEMENTED
// — but purchaseAndVerifyUnlock is only invoked from the native branch of
// handleUnlock, so that's fine.
const AppleIap = registerPlugin('AppleIap')

// contest_class → App Store Connect product ID. IDs are the six
// consumable products that must be created in App Store Connect. They
// must match the server's APPLE_IAP_PRODUCT_* env vars EXACTLY — the
// server cross-references the productId in the verified JWS against
// the pool's contest_class to prevent someone paying $2.99 for a
// daily_pickem SKU and unlocking a $14.99 survivor_season pool.
export const CONTEST_CLASS_TO_APPLE_PRODUCT_ID = {
  daily_pickem:     'com.poolpartysports.unlock.daily_pickem',
  ufc_event:        'com.poolpartysports.unlock.ufc_event',
  weekly_pickem:    'com.poolpartysports.unlock.weekly_pickem',
  golf_pickem:      'com.poolpartysports.unlock.golf_pickem',
  bracket_playoffs: 'com.poolpartysports.unlock.bracket_playoffs',
  survivor_season:  'com.poolpartysports.unlock.survivor_season',
}

/**
 * Run the full Apple IAP unlock flow: derive contest class, look up
 * product ID, purchase via StoreKit, POST jws to the server for
 * signature verification + unlock_group RPC.
 *
 * @param {{
 *   group_id: string,
 *   pool: { sport: string, contest_type: string,
 *           start_date: string|null, end_date: string|null },
 * }} args
 * @returns {Promise<
 *   | { cancelled: true }
 *   | { pending: true }
 *   | { ok: true, group_id: string, transaction_id: string,
 *       was_winning_payment: boolean|null }
 * >}  throws Error on: unmappable contest class, plugin error,
 *      server 4xx/5xx, or missing auth session.
 */
export async function purchaseAndVerifyUnlock({ group_id, pool }) {
  const contestClass = deriveContestClass({
    sport:        pool.sport,
    contest_type: pool.contest_type,
    start_date:   pool.start_date,
    end_date:     pool.end_date,
  })
  if (!contestClass) {
    throw new Error('[stage=derive] Cannot derive contest class for pool')
  }

  const productId = CONTEST_CLASS_TO_APPLE_PRODUCT_ID[contestClass]
  if (!productId) {
    throw new Error(`[stage=map] No Apple product ID for contest class: ${contestClass}`)
  }

  // Native purchase — presents Apple's payment sheet. Wrapped in its
  // own try/catch so a plugin-unimplemented failure (Capacitor didn't
  // discover the Swift plugin at bridge startup) is distinguishable
  // from a StoreKit-inside failure.
  let result
  try {
    result = await AppleIap.purchase({ productId })
  } catch (err) {
    throw new Error(`[stage=plugin-call] ${err?.message || 'plugin call failed'}`)
  }

  if (result?.cancelled) return { cancelled: true }
  if (result?.pending)   return { pending:   true }
  if (result?.error)     throw new Error(`[stage=storekit] ${result.error}`)
  if (!result?.jws)      throw new Error('[stage=no-jws] Purchase returned no JWS')

  // Server verify — mirrors createCheckoutSessionUnlock's auth pattern
  // exactly (supabase session token → Bearer auth → JSON body).
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('[stage=auth] Not authenticated')

  const r = await fetch(`${API_BASE}/api/apple-iap/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ group_id, jws: result.jws }),
  })

  if (!r.ok) {
    const body = await r.json().catch(() => ({}))
    throw new Error(`[stage=server-${r.status}] ${body.error || 'Apple IAP verify failed'}`)
  }

  return await r.json()
}
